import { i as iconDefinition } from './definition28.js';
import { D as DOM, E as ElementStyles, V as VividElement, a as attr, n as nullableNumberConverter, o as observable, h as html, d as createRegisterFunction, f as defineVividComponent } from './vivid-element.js';
import { W as WithLightDOMFeedback, g as generateRandomId, r as renderInLightDOM, f as feedbackMessageDefinition } from './mixins.js';
import { v as visuallyHiddenDefinition } from './definition66.js';
import { D as DelegatesAria, d as delegateAria } from './delegates-aria.js';
import { F as FormAssociated } from './form-associated.js';
import { W as WithCharCount } from './char-count.js';
import { W as WithErrorText } from './with-error-text.js';
import { W as WithSuccessText } from './with-success-text.js';
import { F as FormElement } from './form-element.js';
import { b as AffixIcon, a as affixIconTemplateFactory } from './affix.js';
import { s as styles } from './text-field.js';
import { c as classNames } from './class-names.js';
import { w as when } from './when.js';
import { s as slotted } from './slotted.js';
import { r as ref } from './ref.js';

var __defProp = Object.defineProperty;
var __decorateClass = (decorators, target, key, kind) => {
  var result = void 0 ;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (decorator(target, key, result) ) || result;
  if (result) __defProp(target, key, result);
  return result;
};
const TextFieldType = {
  /**
   * A text TextField
   */
  text: "text"};
const safariWorkaroundClassName = "_vvd-3-text-field-safari-workaround";
const safariWorkaroundStyles = ElementStyles.create([
  `
	.${safariWorkaroundClassName}::placeholder {
		opacity: 1 !important;
		-webkit-text-fill-color: var(--_low-ink-color) !important;
	}
`
]);
const installSafariWorkaroundStyleIfNeeded = (forElement) => {
  const root = forElement.getRootNode();
  if (!safariWorkaroundStyles.isAttachedTo(root)) {
    safariWorkaroundStyles.addStylesTo(root);
  }
};
class TextField extends WithLightDOMFeedback(
  WithCharCount(
    WithErrorText(
      WithSuccessText(
        FormElement(AffixIcon(DelegatesAria(FormAssociated(VividElement))))
      )
    )
  )
) {
  constructor() {
    super(...arguments);
    /**
     * @internal
     */
    this.proxy = document.createElement("input");
    // eslint-disable-next-line @repo/repo/no-attribute-default-value
    this.type = TextFieldType.text;
    this.#randomId = generateRandomId();
  }
  /**
   * @internal
   */
  readOnlyChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.readOnly = this.readOnly;
      this.validate();
    }
  }
  /**
   * @internal
   */
  autofocusChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.autofocus = this.autofocus;
      this.validate();
    }
  }
  /**
   * @internal
   */
  placeholderChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.placeholder = this.placeholder;
    }
  }
  /**
   * @internal
   */
  typeChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.type = this.type;
      this.validate();
    }
  }
  /**
   * @internal
   */
  listChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.setAttribute("list", this.list);
      this.validate();
    }
  }
  /**
   * @internal
   */
  maxlengthChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.maxLength = this.maxlength;
      this.validate();
    }
  }
  /**
   * @internal
   */
  minlengthChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.minLength = this.minlength;
      this.validate();
    }
  }
  /**
   * @internal
   */
  patternChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.pattern = this.pattern;
      this.validate();
    }
  }
  /**
   * @internal
   */
  sizeChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.size = this.size;
    }
  }
  /**
   * @internal
   */
  spellcheckChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.spellcheck = this.spellcheck;
    }
  }
  /**
   * @internal
   */
  valueChanged(previous, next) {
    super.valueChanged(previous, next);
    this._updateControlValueIfNeeded();
    if (this.charCount && this.maxlength) {
      this._updateCharCountRemaining();
    }
  }
  /**
   * Update the controls value only if it is actually different from the actual value.
   * This is important as to not reset the browser's "dirtiness" flag on the input, which is used for min/maxlength
   * constraints.
   * @internal
   */
  _updateControlValueIfNeeded() {
    if (this.control && this.control.value !== this.value) {
      this.control.value = this.value;
    }
  }
  /**
   * Selects all the text in the text field
   *
   * @public
   */
  select() {
    this.control.select();
  }
  /**
   * Handles the internal control's `input` event
   * @internal
   */
  handleTextInput() {
    this.value = this.control.value;
  }
  /**
   * Change event handler for inner control.
   * @remarks
   * "Change" events are not `composable` so they will not
   * permeate the shadow DOM boundary. This fn effectively proxies
   * the change event, emitting a `change` event whenever the internal
   * control emits a `change` event
   * @internal
   */
  handleChange() {
    this.$emit("change");
  }
  /** {@inheritDoc (FormAssociated:interface).validate} */
  validate() {
    super.validate(this.control);
  }
  connectedCallback() {
    super.connectedCallback();
    this.proxy.setAttribute("type", this.type);
    this.validate();
    if (this.autofocus) {
      DOM.queueUpdate(() => {
        this.focus();
      });
    }
    this._updateControlValueIfNeeded();
    installSafariWorkaroundStyleIfNeeded(this);
    this._renderCharCountRemaining();
  }
  focus() {
    this.control?.focus();
  }
  #randomId;
  /**
   * @internal
   */
  get _uniqueId() {
    return this.id || this.#randomId;
  }
}
__decorateClass([
  attr({ attribute: "readonly", mode: "boolean" })
], TextField.prototype, "readOnly");
__decorateClass([
  attr({ mode: "boolean" })
], TextField.prototype, "autofocus");
__decorateClass([
  attr
], TextField.prototype, "placeholder");
__decorateClass([
  attr
], TextField.prototype, "type");
__decorateClass([
  attr
], TextField.prototype, "list");
__decorateClass([
  attr({ converter: nullableNumberConverter })
], TextField.prototype, "maxlength");
__decorateClass([
  attr({ converter: nullableNumberConverter })
], TextField.prototype, "minlength");
__decorateClass([
  attr
], TextField.prototype, "pattern");
__decorateClass([
  attr({ converter: nullableNumberConverter })
], TextField.prototype, "size");
__decorateClass([
  attr({ mode: "boolean" })
], TextField.prototype, "spellcheck");
__decorateClass([
  attr
], TextField.prototype, "appearance");
__decorateClass([
  attr
], TextField.prototype, "shape");
__decorateClass([
  attr
], TextField.prototype, "autoComplete");
__decorateClass([
  attr()
], TextField.prototype, "scale");
__decorateClass([
  attr({ attribute: "inputmode" })
], TextField.prototype, "inputMode");
__decorateClass([
  observable
], TextField.prototype, "actionItemsSlottedContent");
__decorateClass([
  observable
], TextField.prototype, "leadingActionItemsSlottedContent");

const getControlId = (id) => `vvd-text-field-control-${id}`;
const getStateClasses = ({
  errorValidationMessage,
  disabled,
  value,
  readOnly,
  placeholder,
  appearance,
  shape,
  label,
  successText,
  actionItemsSlottedContent,
  leadingActionItemsSlottedContent,
  icon,
  scale
}) => classNames(
  ["error", Boolean(errorValidationMessage)],
  ["disabled", disabled],
  ["has-value", Boolean(value)],
  ["readonly", readOnly],
  ["placeholder", Boolean(placeholder)],
  [`appearance-${appearance}`, Boolean(appearance)],
  [`shape-${shape}`, Boolean(shape)],
  ["no-label", !label],
  ["has-icon", !!icon],
  ["success", Boolean(successText)],
  ["action-items", !!actionItemsSlottedContent?.length],
  ["leading-action-items", !!leadingActionItemsSlottedContent?.length],
  ["no-leading", !(leadingActionItemsSlottedContent?.length || icon)],
  [`size-${scale}`, Boolean(scale)]
);
const TextfieldTemplate = (context) => {
  const affixIconTemplate = affixIconTemplateFactory(context);
  return html`
		<div class="base ${getStateClasses}">
			${when(
    (x) => x.charCount && x.maxlength,
    (x) => x._getCharCountTemplate(context)
  )}
			<slot class="label" name="_label"></slot>
			${renderInLightDOM(html`
				${when(
    (x) => x.label,
    html`
						<label slot="_label" for="${(x) => getControlId(x._uniqueId)}">
							${(x) => x.label}
						</label>
					`
  )}
			`)}
			<div class="fieldset">
				<div class="leading-items-wrapper">
					<slot
						name="leading-action-items"
						${slotted("leadingActionItemsSlottedContent")}
					></slot>
					${(x) => affixIconTemplate(x.icon)}
				</div>
				<div class="wrapper">
					<slot class="control" name="_control"></slot>
					${renderInLightDOM(
    html`<input
							slot="_control"
							id="${(x) => getControlId(x._uniqueId)}"
							class="_vvd-3-text-field-safari-workaround"
							@input="${(x) => x.handleTextInput()}"
							@change="${(x) => x.handleChange()}"
							@blur="${(x) => {
      x.$emit("blur", void 0, { bubbles: false });
    }}"
							@focus="${(x) => {
      x.$emit("focus", void 0, { bubbles: false });
    }}"
							?autofocus="${(x) => x.autofocus}"
							?disabled="${(x) => x.disabled}"
							?readonly="${(x) => x.readOnly}"
							?required="${(x) => x.required}"
							?spellcheck="${(x) => x.spellcheck}"
							list="${(x) => x.list}"
							maxlength="${(x) => x.maxlength}"
							minlength="${(x) => x.minlength}"
							pattern="${(x) => x.pattern}"
							placeholder="${(x) => x.placeholder}"
							size="${(x) => x.size}"
							autocomplete="${(x) => x.autoComplete}"
							type="${(x) => x.type}"
							inputmode="${(x) => x.inputMode}"
							aria-describedby="${(x) => x._feedbackDescribedBy} ${(x) => x.charCount && x.maxlength ? x._charCountDescribedBy : null}"
							value="${(x) => x.initialValue}"
							${delegateAria()}
							${ref("control")}
						/>`
  )}
				</div>
				<div class="action-items-wrapper">
					<slot
						name="action-items"
						${slotted("actionItemsSlottedContent")}
					></slot>
				</div>
			</div>
			${(x) => x._getFeedbackTemplate(context)}
		</div>
	`;
};

const textFieldDefinition = defineVividComponent(
  "text-field",
  TextField,
  TextfieldTemplate,
  [iconDefinition, feedbackMessageDefinition, visuallyHiddenDefinition],
  {
    styles,
    shadowOptions: {
      delegatesFocus: true
    }
  }
);
const registerTextField = createRegisterFunction(textFieldDefinition);

export { TextField as T, registerTextField as r, textFieldDefinition as t };
